/**
 * Razorpay checkout helper.
 * Opens the Razorpay modal and resolves with the verified result on success,
 * or rejects on failure / dismissed.
 */
import { apiClient } from "./api";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.getElementById("razorpay-script");
    if (!existing) {
      const s = document.createElement("script");
      s.id = "razorpay-script";
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.async = true;
      document.body.appendChild(s);
    }
    const wait = (n = 0) => {
      if (window.Razorpay) return resolve(true);
      if (n > 40) return resolve(false);
      setTimeout(() => wait(n + 1), 100);
    };
    wait();
  });
}

/**
 * Subscription checkout.
 * @param {{plan: 'pro'|'elite', billing_cycle: 'monthly'|'annual'}} params
 */
export async function payForSubscription(params) {
  const ok = await loadRazorpay();
  if (!ok) throw new Error("Razorpay failed to load");

  const { data: order } = await apiClient.post(
    "/payments/subscription/create-order",
    params
  );

  return openCheckout({
    order,
    description: `${params.plan.toUpperCase()} · ${params.billing_cycle}`,
    intent: "subscription",
  });
}

/**
 * Trainer booking checkout.
 * @param {{trainer_id: string, tier_index: number, slot_day?: string, slot_time?: string}} params
 */
export async function payForBooking(params) {
  const ok = await loadRazorpay();
  if (!ok) throw new Error("Razorpay failed to load");

  const { data: order } = await apiClient.post(
    "/payments/booking/create-order",
    params
  );

  return openCheckout({
    order,
    description: `${order.trainer_name} · ${order.tier_name}`,
    intent: "booking",
  });
}

/**
 * Marketplace checkout.
 * @param {{use_fitpoints: boolean, delivery_type: 'delivery'|'pickup', delivery_address: object}} params
 */
export async function payForMarketplace(params) {
  const ok = await loadRazorpay();
  if (!ok) throw new Error("Razorpay failed to load");

  const { data: order } = await apiClient.post(
    "/marketplace/orders/create",
    params
  );

  return new Promise((resolve, reject) => {
    const options = {
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: "FitFrik Shop",
      description: "Marketplace order",
      order_id: order.order_id,
      prefill: { name: order.name || "", email: order.email || "" },
      notes: { intent: "marketplace", intent_id: order.intent_id },
      theme: { color: "#00D4FF" },
      handler: async (response) => {
        try {
          const { data: verify } = await apiClient.post("/marketplace/orders/verify", {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            fitfrik_order_id: order.intent_id,
          });
          resolve({ ...verify, fitfrik_order_id: order.intent_id });
        } catch (e) {
          reject(new Error(e.response?.data?.detail || "Verification failed"));
        }
      },
      modal: { ondismiss: () => reject(new Error("dismissed")) },
    };
    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (resp) => reject(new Error(resp.error?.description || "Payment failed")));
    rzp.open();
  });
}

function openCheckout({ order, description, intent }) {
  return new Promise((resolve, reject) => {
    const options = {
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: "FitFrik",
      description,
      order_id: order.order_id,
      prefill: {
        name: order.name || "",
        email: order.email || "",
      },
      notes: {
        intent,
        intent_id: order.intent_id,
      },
      theme: { color: "#00D4FF" },
      handler: async (response) => {
        try {
          const { data: verify } = await apiClient.post("/payments/verify", {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            intent,
            intent_id: order.intent_id,
          });
          resolve(verify);
        } catch (e) {
          reject(new Error(e.response?.data?.detail || "Verification failed"));
        }
      },
      modal: {
        ondismiss: () => reject(new Error("dismissed")),
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (resp) => {
      reject(new Error(resp.error?.description || "Payment failed"));
    });
    rzp.open();
  });
}
